import type { Writable } from "@glideapps/ts-necessities";
import { assert, definedMap } from "@glideapps/ts-necessities";
import deepEqual from "deep-equal";
import last from "lodash/last";
import reverse from "lodash/reverse";
import sortBy from "lodash/sortBy";
import toPairs from "lodash/toPairs";
import type { ComponentIndexes } from "../common/component-indexes";
import type { AppDescription } from "../common/components/SerializedApp";
import { getAppTabs } from "../common/components/SerializedApp";
import type { MissingTablesAndColumns } from "../common/Database";
import type { TableName, UniversalTableName } from "../common/database-strings";
import { areTableNamesEqual, getDebugPrintTableName } from "../common/database-strings";
import type {
    ActionDescription,
    ArrayFilter,
    ArrayTransform,
    ClassOrArrayScreenDescription,
    ColumnAssignment,
    ColumnAssignmentsDescription,
    ColumnAssignmentsWithLegacyDescription,
    ColumnType,
    ColumnTypeKind,
    Description,
    InputOutputTables,
    LegacyPropertyDescription,
    PropertyDescription,
    PropertyKind,
    ScreenDescription,
    SourceColumn,
    TabDescription,
    TableColumn,
    TableGlideType,
    TableRefGlideType,
    TitleDescription,
    TransformableContentDescription,
    TypeSchema,
} from "../common/description";
import {
    ActionKind,
    BinaryPredicateFormulaOperator,
    findTable,
    getArrayTransforms,
    getEmailOwnersColumnNames,
    getNonHiddenColumns,
    getSourceColumnPath,
    getTableColumn,
    getTableColumnDisplayName,
    getTableName,
    getTableRefTableName,
    ImageKind,
    isComputedColumn,
    isDataSourceColumn,
    isDateOrDateTimeTypeKind,
    isEmailArrayFilter,
    isFormScreen,
    isLinkTypeKind,
    isNumberTypeKind,
    isPrimitiveType,
    isSingleRelationType,
    isSourceColumn,
    isStringTypeKind,
    isStringTypeOrStringTypeArray,
    makeInputOutputTables,
    ScreenDescriptionKind,
    SpecialValueKind,
} from "../common/description";
import { getFeatureSetting } from "../common/feature-settings";
import { isFavoritedColumnName } from "../common/google-sheets";
import { imageProperties, isImageType, titleProperties } from "../component-utils/description-utils";
import { getMenuScreenName } from "../component-utils/screen-names";
import { areSetsEqual, assertNever, defined, mapFilterUndefined, shallowEqualArrays } from "../support";
import { defaultActionForColumn } from "./actions";
import type {
    ActionPropertyDescriptor,
    ActionsRecord,
    ColumnTypePredicate,
    PropertyDescriptor,
    PropertyDescriptorCase,
} from "./components";
import {
    getPropertyDescription,
    getPropertyDescriptionValue,
    isMultiCasePropertyDescriptor,
    isNamedPropertySource,
} from "./components";
import {
    getActionProperty,
    getColumnProperty,
    getEnumProperty,
    getFilterProperty,
    getScreenProperty,
    getSourceColumnProperty,
    getStringProperty,
    makeColumnProperty,
    makeEnumProperty,
    makeSpecialValueProperty,
} from "./components/property-utils";
import { MissingTablesAndColumnsAccumulator } from "./missing-tables-and-columns";

// `preferredDisplayNames` must be lowercase. `excludeNames` must have exact
// case, since it contains table names, not display names.
export function findFittingColumn(
    acceptedColumnns: ReadonlyArray<TableColumn>,
    excludeColumn: TableColumn | undefined,
    preferredDisplayNames: ReadonlyArray<string>,
    avoidColumnNames: ReadonlySet<string> | undefined,
    avoidColumnNamesSecondary: ReadonlySet<string> | undefined,
    excludeNames: ReadonlySet<string> | undefined,
    preferredType: ((t: ColumnType) => boolean) | ColumnTypeKind | undefined,
    excludeWrongPrimitiveTypes: boolean
): TableColumn | undefined {
    // FIXME: Don't use strict sorting, but give scores.  A full name match
    // would score high, a prefix or postfix match would score medium, and
    // a partial match in the middle would score low, for example.
    const preferType = typeof preferredType === "string" ? (t: ColumnType) => t.kind === preferredType : preferredType;
    let excludeType: ((t: ColumnType) => boolean) | undefined;
    if (excludeWrongPrimitiveTypes) {
        if (preferredType === "string") {
            excludeType = ({ kind }: ColumnType) =>
                isLinkTypeKind(kind) || isDateOrDateTimeTypeKind(kind) || isNumberTypeKind(kind);
        } else if (preferredType === "image-uri") {
            excludeType = ({ kind }: ColumnType) => kind !== "image-uri";
        } else if (typeof preferredType === "string" && isLinkTypeKind(preferredType)) {
            excludeType = ({ kind }: ColumnType) => !isLinkTypeKind(kind);
        }
    }
    const filtered = reverse(Array.from(acceptedColumnns)).filter(
        c =>
            c !== excludeColumn &&
            (excludeNames === undefined || !excludeNames.has(c.name)) &&
            excludeType?.(c.type) !== true
    );
    const sorted = sortBy(
        filtered,
        (c: TableColumn) => avoidColumnNames === undefined || !avoidColumnNames.has(c.name),
        (c: TableColumn) => avoidColumnNamesSecondary === undefined || !avoidColumnNamesSecondary.has(c.name),
        (c: TableColumn) => {
            let idx = preferredDisplayNames.indexOf(getTableColumnDisplayName(c).toLowerCase());
            if (idx < 0) idx = preferredDisplayNames.length;
            return -idx;
        },
        (c: TableColumn) => preferType === undefined || preferType(c.type),
        (c: TableColumn) => defaultActionForColumn(c) !== undefined
    );
    return reverse(sorted)[0];
}

export function findFittingColumnInTable(
    table: TableGlideType,
    preferredDisplayNames: ReadonlyArray<string>,
    excludeNames: ReadonlySet<string> | undefined,
    preferredType: ColumnTypePredicate | undefined,
    acceptedType: ColumnTypePredicate,
    excludeWrongPrimitiveTypes: boolean
): TableColumn | undefined {
    const acceptType = typeof acceptedType === "string" ? (t: ColumnType) => t.kind === acceptedType : acceptedType;
    const acceptedColumns = getNonHiddenColumns(table).filter(c => c.isUserSpecific !== true && acceptType(c.type));
    const avoid = definedMap(table.rowIDColumn, n => getTableColumn(table, n));
    return findFittingColumn(
        acceptedColumns,
        avoid,
        preferredDisplayNames,
        undefined,
        undefined,
        excludeNames,
        preferredType,
        excludeWrongPrimitiveTypes
    );
}

function findFittingProperty(
    table: TableGlideType,
    preferredDisplayNames: ReadonlyArray<string>,
    excludeNames: ReadonlySet<string> | undefined,
    preferredType: ColumnTypePredicate | undefined,
    acceptedType: ColumnTypePredicate
): string | undefined {
    return definedMap(
        findFittingColumnInTable(table, preferredDisplayNames, excludeNames, preferredType, acceptedType, false),
        c => c.name
    );
}

function findImageColumn(
    table: TableGlideType,
    mustBeImageURI: boolean,
    forListItem: boolean,
    allowArrayColumn: boolean,
    excludeColumns: ReadonlySet<string> | undefined
): TableColumn | undefined {
    const isImage = (t: ColumnType) => isImageType(t, forListItem, allowArrayColumn);
    return findFittingColumnInTable(
        table,
        imageProperties,
        excludeColumns,
        isImage,
        mustBeImageURI ? isImage : allowArrayColumn ? isStringTypeOrStringTypeArray : t => isStringTypeKind(t.kind),
        true
    );
}

// FIXME: Implement this via the summary property descriptors
export function propertiesInSummary(desc: TitleDescription): string[] {
    return mapFilterUndefined([desc.titleProperty, desc.subtitleProperty, desc.imageURLProperty], getColumnProperty);
}

function titleColumnForTable(
    table: TableGlideType,
    mustBeCorrectType: boolean,
    excludeColumns: ReadonlySet<string> | undefined
): string | undefined {
    return findFittingProperty(
        table,
        titleProperties,
        excludeColumns,
        "string",
        mustBeCorrectType ? "string" : t => isPrimitiveType(t) && !isLinkTypeKind(t.kind)
    );
}

export function summaryColumnsForTable(
    table: TableGlideType,
    titleRequired: boolean,
    makeSummaryIfPossible: boolean,
    forListItem: boolean = false,
    allowArrayColumn: boolean = true,
    excludeColumns: ReadonlySet<string> | undefined = undefined
): { [K in keyof TitleDescription]: PropertyDescription | undefined } | undefined {
    const titleProperty = titleColumnForTable(table, !makeSummaryIfPossible, excludeColumns);
    if (titleRequired && titleProperty === undefined) return undefined;

    const exclude = new Set(excludeColumns);

    let subtitleProperty: string | undefined;
    if (titleProperty !== undefined) {
        exclude.add(titleProperty);
        subtitleProperty = titleColumnForTable(table, !makeSummaryIfPossible, exclude);
    } else {
        subtitleProperty = undefined;
    }

    if (subtitleProperty !== undefined) {
        exclude.add(subtitleProperty);
    }

    const imageURLProperty = definedMap(
        findImageColumn(table, true, forListItem, allowArrayColumn, exclude),
        c => c.name
    );

    if (titleProperty === undefined && subtitleProperty === undefined && imageURLProperty === undefined) {
        return undefined;
    }

    if (imageURLProperty !== undefined) {
        exclude.add(imageURLProperty);
    }

    return {
        titleProperty: makeColumnProperty(titleProperty),
        subtitleProperty: makeColumnProperty(subtitleProperty),
        imageURLProperty: makeColumnProperty(imageURLProperty),
        imageKind: makeEnumProperty(imageURLProperty !== undefined ? ImageKind.URL : undefined),
    };
}

export function getScreenFilter(desc: ScreenDescription): {
    filter: ArrayFilter | undefined;
    transforms: readonly ArrayTransform[];
} {
    if (desc.kind !== ScreenDescriptionKind.Class && desc.kind !== ScreenDescriptionKind.Array) {
        return { filter: undefined, transforms: [] };
    }
    const filter = getFilterProperty(desc.filter);
    const transforms = getArrayTransforms(desc);
    return { filter, transforms };
}

// Returns `true` if the filter columns exist, otherwise a set of the ones that
// are missing
export function doFilterColumnsExist(filter: ArrayFilter, table: TableGlideType): true | Set<string> {
    const nonExisting = new Set<string>();
    const valueColumn = getColumnProperty(filter.value as PropertyDescription);
    if (valueColumn !== undefined) {
        const column = getTableColumn(table, valueColumn);
        if (column === undefined) {
            nonExisting.add(valueColumn);
        }
    }

    const keyColumn = getColumnProperty(filter.key as PropertyDescription);
    if (keyColumn !== undefined) {
        const column = getTableColumn(table, keyColumn);
        if (column === undefined) {
            nonExisting.add(keyColumn);
        }
    }

    if (nonExisting.size === 0) {
        return true;
    }
    return nonExisting;
}

export function makeInputOutputTablesForClassOrArrayScreen(
    desc: ClassOrArrayScreenDescription,
    getTable: (tableRef: TableRefGlideType) => TableGlideType | undefined
): InputOutputTables | undefined {
    if (!isSingleRelationType(desc.type)) return undefined;
    const table = getTable(desc.type);
    if (table === undefined) return undefined;

    let outputTable: TableGlideType;
    if (isFormScreen(desc)) {
        const t = getTable(desc.formType);
        if (t === undefined) return undefined;
        outputTable = t;
    } else {
        outputTable = table;
    }

    return makeInputOutputTables(table, outputTable);
}

export function inputOutputTablesForClassOrArrayScreen(
    desc: ClassOrArrayScreenDescription,
    schema: TypeSchema
): InputOutputTables | undefined {
    return makeInputOutputTablesForClassOrArrayScreen(desc, r => findTable(schema, r));
}

export function getPropertyDescriptorCaseForKind(
    kind: PropertyKind,
    descr: PropertyDescriptor
): PropertyDescriptorCase | undefined {
    if (isMultiCasePropertyDescriptor(descr)) {
        return descr.cases.find(c => c.kind === kind);
    } else if (descr.kind === kind) {
        return descr;
    } else {
        return undefined;
    }
}

export function getPropertyDescriptorCaseForDescription(
    desc: PropertyDescription,
    descr: PropertyDescriptor
): PropertyDescriptorCase | undefined {
    return getPropertyDescriptorCaseForKind(desc.kind, descr);
}

export function getSummarySearchProperties(desc: TitleDescription): readonly string[] {
    const searchProperties: string[] = [];
    const titleProperty = definedMap(desc.titleProperty, getColumnProperty);
    const subtitleProperty = definedMap(desc.subtitleProperty, getColumnProperty);
    if (titleProperty !== undefined) {
        searchProperties.push(titleProperty);
    }
    if (subtitleProperty !== undefined) {
        searchProperties.push(subtitleProperty);
    }
    return searchProperties;
}

export function getPropertyAndDescriptorFromDescription<T>(
    desc: Description,
    pdescr: PropertyDescriptor
):
    | {
          description: PropertyDescription;
          descriptorCase: PropertyDescriptorCase;
      }
    | undefined {
    const pd = getPropertyDescription<T>(desc, pdescr);
    if (pd === undefined) return undefined;

    const pdc = getPropertyDescriptorCaseForDescription(pd, pdescr);
    if (pdc === undefined) return undefined;

    return { description: pd, descriptorCase: pdc };
}

export function getTableRefForScreen(screen: ScreenDescription): TableRefGlideType | undefined {
    if (screen.kind !== ScreenDescriptionKind.Class && screen.kind !== ScreenDescriptionKind.Array) return undefined;

    const type = screen.type;
    if (isSingleRelationType(type)) {
        return type;
    } else {
        return undefined;
    }
}

export function areColumnTypesEqualExceptPrimitives(t1: ColumnType, t2: ColumnType): boolean {
    if (isPrimitiveType(t1)) {
        return isPrimitiveType(t2);
    } else if (t1.kind === "array") {
        if (t2.kind !== "array") return false;
        if (isPrimitiveType(t1.items)) {
            return isPrimitiveType(t2.items);
        } else if (isSingleRelationType(t1.items)) {
            return (
                isSingleRelationType(t2.items) &&
                areTableNamesEqual(getTableRefTableName(t1.items), getTableRefTableName(t2.items))
            );
        } else {
            assertNever(t1.items);
        }
    } else if (isSingleRelationType(t1)) {
        return isSingleRelationType(t2) && areTableNamesEqual(getTableRefTableName(t1), getTableRefTableName(t2));
    } else {
        assertNever(t1);
    }
}

const defaultMetadataDifferences = {
    oneIsMissing: false,
    numRows: false,
    sheetMetadata: false,
    rowIDColumn: false,
    tableOrder: false,
    columnDisplayName: false,
    columnReadonly: false,
    columnFromNativeTable: false,
    columnFromExternalSource: false,
};

// We don't want to redraw the builder if only the number of rows changed.
export function doMetadataDifferencesRequireBuilderUpdate(d: MetadataDifferences): boolean {
    return (
        d.oneIsMissing || d.sheetMetadata || d.rowIDColumn || d.tableOrder || d.columnDisplayName || d.columnReadonly
    );
}

type MetadataDifferences = Record<keyof typeof defaultMetadataDifferences, boolean>;

export interface SchemaDifferences {
    readonly metadata: MetadataDifferences | undefined;
    readonly tablesOrColumns: boolean;
    readonly tablesAdded: boolean;
    // Not an exhaustive list
    readonly issues: readonly string[];
    readonly missing: MissingTablesAndColumns | undefined;
}

// Unfortunately we can't compare schemas with `deepEqual` because they
// contain ancillary information, in particular row counts. "metadata" means
// only metadata changed.  NOTE: The Row ID column counts as only metadata.
export function compareSchemas(
    schema1: TypeSchema | undefined,
    schema2: TypeSchema | undefined,
    accumulateMissing: boolean,
    areColumnTypesEqual?: (t1: ColumnType, t2: ColumnType) => boolean
): SchemaDifferences | undefined {
    let metadataDifferences: MetadataDifferences | undefined;

    function addMetadataDifference(diff: Partial<MetadataDifferences>) {
        if (metadataDifferences === undefined) {
            metadataDifferences = defaultMetadataDifferences;
        }
        metadataDifferences = { ...metadataDifferences, ...diff };
    }

    if (schema1 === undefined || schema2 === undefined) {
        addMetadataDifference({ oneIsMissing: true });
        return schema1 === schema2
            ? undefined
            : {
                  metadata: metadataDifferences,
                  tablesOrColumns: true,
                  tablesAdded: true,
                  issues: ["One schema is undefined"],
                  missing: undefined,
              };
    }

    if (schema1 === schema2) return undefined;

    if (areColumnTypesEqual === undefined) {
        areColumnTypesEqual = (t1, t2) => deepEqual(t1, t2, { strict: true });
    }

    const missing = accumulateMissing ? new MissingTablesAndColumnsAccumulator() : undefined;

    const tablesAdded = schema2.tables.some(t => findTable(schema1, getTableName(t)) === undefined);
    let tablesOrColumns = tablesAdded;
    const issues: string[] = [];

    for (const table1 of schema1.tables) {
        const tableName = getTableName(table1);
        const table2 = findTable(schema2, tableName);
        if (table2 === undefined) {
            tablesOrColumns = true;
            issues.push(`Table ${tableName.name} not found in new schema`);
            missing?.addTable(table1);
            continue;
        }

        if (table1.numDataRows !== table2.numDataRows) {
            addMetadataDifference({ numRows: true });
            issues.push(`Numbers of data rows differ in ${tableName.name}`);
        }

        if (table1.sheetID !== table2.sheetID || table1.sheetName !== table2.sheetName) {
            addMetadataDifference({ sheetMetadata: true });
            issues.push(`Sheet metadata differs in ${tableName.name}`);
        }

        if (table1.rowIDColumn !== table2.rowIDColumn) {
            addMetadataDifference({ rowIDColumn: true });
            issues.push(`Row ID columns differ in ${tableName.name}`);
        }

        if (schema1.tables.indexOf(table1) !== schema2.tables.indexOf(table2)) {
            addMetadataDifference({ tableOrder: true });
            issues.push(`Index of table ${tableName.name} differs`);
        }

        if (!areSetsEqual(getEmailOwnersColumnNames(table1), getEmailOwnersColumnNames(table2))) {
            tablesOrColumns = true;
            issues.push(`Email owners columns differ in ${tableName.name}`);
        }

        if (table1.columns.length !== table2.columns.length) {
            tablesOrColumns = true;
            issues.push(`Number of columns differ in ${tableName.name}`);
        }

        if (table1.columns.length === table2.columns.length || accumulateMissing) {
            for (const column1 of table1.columns) {
                const column2 = getTableColumn(table2, column1.name);
                if (column2 === undefined) {
                    tablesOrColumns = true;
                    issues.push(`Column ${column1.name} not found in ${tableName.name}`);
                    missing?.addColumn(table1, column1.name);
                    continue;
                }

                if (column1.displayName !== column2.displayName) {
                    addMetadataDifference({ columnDisplayName: true });
                    // if (column1.name !== isFavoritedColumnName) {
                    issues.push(`Column display names differ for ${column1.name} in ${tableName.name}`);
                    // }
                }

                if (!areColumnTypesEqual(column1.type, column2.type)) {
                    tablesOrColumns = true;
                    issues.push(
                        `Column types of ${column1.name} differ in ${tableName.name}.  Kind was ${JSON.stringify(
                            column1.type
                        )}, is ${JSON.stringify(column2.type)}.`
                    );
                    // }
                    continue;
                }

                if ((column1.hidden === true) !== (column2.hidden === true)) {
                    tablesOrColumns = true;
                    if (column1.name !== isFavoritedColumnName) {
                        issues.push(`Column ${column1.name} hidden is different in ${tableName.name}`);
                    }
                }

                if ((column1.isProtected === true) !== (column2.isProtected === true)) {
                    tablesOrColumns = true;
                    if (column1.name !== isFavoritedColumnName) {
                        issues.push(`Column ${column1.name} protected is different in ${tableName.name}`);
                    }
                }

                if (
                    (column1.isUserSpecific === true) !== (column2.isUserSpecific === true) ||
                    !deepEqual(column1.formula, column2.formula, { strict: true }) ||
                    !deepEqual(column1.displayFormula, column2.displayFormula, { strict: true })
                ) {
                    tablesOrColumns = true;
                    issues.push(`Column ${column1.name} differs in ${tableName.name}`);
                    continue;
                }

                if ((column1.isReadOnly === true) !== (column2.isReadOnly === true)) {
                    addMetadataDifference({ columnReadonly: true });
                    issues.push(`Column ${column1.name} read-only is different in ${tableName.name}`);
                }

                if ((column1.fromNativeTable === true) !== (column2.fromNativeTable === true)) {
                    addMetadataDifference({ columnFromNativeTable: true });
                    issues.push(`Column ${column1.name} from native table is different in ${tableName.name}`);
                }

                if ((column1.fromExternalSource === true) !== (column2.fromExternalSource === true)) {
                    addMetadataDifference({ columnFromExternalSource: true });
                    issues.push(`Column ${column1.name} from external source is different in ${tableName.name}`);
                }
            }
        }
    }

    if (metadataDifferences === undefined && !tablesOrColumns && !tablesAdded) return undefined;

    return { metadata: metadataDifferences, tablesOrColumns, tablesAdded, issues, missing: missing?.getMissing() };
}

export function setEmailOwnersColumns(
    schema: TypeSchema,
    updates: readonly (readonly [TableName, readonly string[]])[]
): [TypeSchema, readonly TableName[]] | number {
    const tablesToUpdate: TableName[] = [];

    const newTables: TableGlideType[] = [];
    for (const table of schema.tables) {
        const tableName = getTableName(table);
        if (tableName.isSpecial) {
            newTables.push(table);
            continue;
        }

        const update = updates.find(u => areTableNamesEqual(tableName, u[0]));
        if (update === undefined) {
            newTables.push(table);
            continue;
        }

        const columnNames = update[1];

        let newTable = { ...table };

        for (const columnName of columnNames) {
            const column = getTableColumn(table, columnName);
            if (column === undefined) return 400;
            if (!isStringTypeOrStringTypeArray(column.type)) return 400;
        }

        newTable = { ...newTable, emailOwnersColumn: columnNames };

        newTables.push(newTable);
        tablesToUpdate.push(tableName);
    }

    return [{ ...schema, tables: newTables }, tablesToUpdate];
}

export function makeMissingColumnMessage(column: string | SourceColumn): string | undefined {
    if (!getFeatureSetting("showMissingEntities")) return undefined;
    if (isSourceColumn(column)) {
        column = defined(last(getSourceColumnPath(column)));
    }
    return `The column "${column}" doesn't exist anymore`;
}

export function makeMissingTableMessage(tableName: UniversalTableName): string | undefined {
    if (!getFeatureSetting("showMissingEntities")) return undefined;
    return `The sheet "${getDebugPrintTableName(tableName)}" doesn't exist anymore`;
}

export function makeEmailArrayFilter(columnName: string): ArrayFilter {
    return {
        key: makeSpecialValueProperty(SpecialValueKind.VerifiedEmailAddress),
        operator: BinaryPredicateFormulaOperator.Equals,
        value: makeColumnProperty(columnName),
    };
}

export function getEmailArrayFilter(desc: TransformableContentDescription): ArrayFilter | undefined {
    const filter = getFilterProperty(desc.filter);
    if (filter === undefined || !isEmailArrayFilter(filter)) return undefined;
    return filter;
}

export function canBeOwnersColumn(column: TableColumn): boolean {
    if (isComputedColumn(column) || column.isUserSpecific === true) return false;
    return isStringTypeOrStringTypeArray(column.type);
}

export function canColumnBeProtected(column: TableColumn, isNativeTable: boolean): boolean {
    if (isNativeTable) return false;
    return isDataSourceColumn(column, isNativeTable);
}

export function makeInputOutputTablesForFormOnSubmitAction(screenTables: InputOutputTables): InputOutputTables {
    // This is one readonly why there is no ##onSubmitMutatingScreenKind -
    // there's only the output row.
    return makeInputOutputTables(screenTables.output);
}

export function getColumnNameAndGroup(c: TableColumn): [string | undefined, string] {
    const name = getTableColumnDisplayName(c);

    if (!getFeatureSetting("columnGrouping")) {
        return [undefined, name];
    }

    const idx = name.indexOf("/");
    if (idx < 0) {
        return [undefined, name];
    } else {
        return [name.substring(0, idx).trimRight(), name.substring(idx + 1).trimLeft()];
    }
}

function removeEmptyColumnAssignments(assignments: readonly ColumnAssignment[]): readonly ColumnAssignment[] {
    // ##emptyColumnAssignments:
    // An empty "Custom" assignment will write a string property with an empty
    // string. Those count as if they weren't present for set/add row actions.
    return assignments.filter(a => getStringProperty(a.value)?.trim() !== "");
}

export function getColumnAssignments(screen: ColumnAssignmentsDescription): readonly ColumnAssignment[] {
    return removeEmptyColumnAssignments(screen.columnAssignments ?? []);
}

export function getColumnAssignmentsWithLegacy(
    desc: ColumnAssignmentsWithLegacyDescription
): readonly ColumnAssignment[] {
    const columnAssignments = Array.from(getColumnAssignments(desc));
    for (const [destColumn, value] of toPairs(desc.columns)) {
        // Don't generate duplicates. We had a bug where we would keep the old
        // ones, but ignore them, and let users add new ones, so the new-style
        // assignments have priority.
        if (columnAssignments.some(ca => ca.destColumn === destColumn)) continue;
        columnAssignments.push({ destColumn, value });
    }
    return removeEmptyColumnAssignments(columnAssignments);
}

export function getActionPropertyWithDescriptor(
    desc: Description,
    descr: ActionPropertyDescriptor
): ActionDescription | undefined {
    // This is where we handle the special case for ##inlineListDefaultAction
    // where we treat `undefined` as the default action.
    const { defaultActionForUndefined } = descr;
    let hadValue = false;

    let action = getPropertyDescriptionValue(desc, descr, pd => {
        hadValue = true;
        return getActionProperty(pd);
    });

    if (action !== undefined && action.kind !== ActionKind.Compound && !descr.kinds.includes(action.kind)) {
        action = undefined;
    }

    if ((action === undefined && descr.required === true) || (defaultActionForUndefined === true && !hadValue)) {
        action = definedMap(descr.kinds[0], k => ({ kind: k }));
    }

    return action;
}

export type ActionsWithDescriptors = readonly [
    descriptor: ActionPropertyDescriptor,
    action: ActionDescription | undefined,
    name: string
][];

export function getActionsForDescriptors(
    desc: Description,
    descriptors: readonly ActionPropertyDescriptor[]
): ActionsWithDescriptors {
    const pairs: [ActionPropertyDescriptor, ActionDescription | undefined, string][] = [];
    for (const descr of descriptors) {
        assert(isNamedPropertySource(descr.property));
        const action = getActionPropertyWithDescriptor(desc, descr);
        pairs.push([descr, action, descr.property.name]);
    }
    return pairs;
}

export function getActionsRecord(desc: Description, descriptors: readonly ActionPropertyDescriptor[]): ActionsRecord {
    const record: Writable<ActionsRecord> = { actions: [] };

    for (const [, action, name] of getActionsForDescriptors(desc, descriptors)) {
        if (action !== undefined) {
            record[name] = [action];
        } else {
            record[name] = [];
        }
    }

    return record;
}

export function areComponentIndexesEqual(a?: ComponentIndexes, b?: ComponentIndexes): boolean {
    if (a === undefined && b === undefined) return true;
    if (a === undefined || b === undefined) return false;
    return shallowEqualArrays(a, b);
}

export function previousComponentIndex(indexes: ComponentIndexes): ComponentIndexes | undefined {
    if (indexes.length === 1) {
        if (indexes[0] === 0) return undefined;
        return [indexes[0] - 1];
    } else if (indexes.length === 2) {
        if (indexes[1] === 0) {
            return [indexes[0]];
        } else {
            return [indexes[0], indexes[1] - 1];
        }
    } else {
        return assertNever(indexes);
    }
}

export function getMenuScreenNames(appDescription: AppDescription, menuID: string): readonly string[] {
    const prefix = getMenuScreenName(menuID, undefined);
    const screenNames: string[] = [];
    for (const screenName of Object.keys(appDescription.screenDescriptions)) {
        if (screenName.startsWith(prefix)) {
            screenNames.push(screenName);
        }
    }
    return screenNames;
}

export function findTabInAppDescription(
    tabScreenName: string,
    appDescription: AppDescription
): [TabDescription, number] | undefined {
    const tabs = getAppTabs(appDescription);
    const index = tabs.findIndex(t => getScreenProperty(t.screenName) === tabScreenName);
    if (index < 0) return undefined;
    return [tabs[index], index];
}

// `true` means "this item".
export function getSingleRelationOrThis(pd: LegacyPropertyDescription | undefined): SourceColumn | true | undefined {
    const sourceColumn = getSourceColumnProperty(pd);
    if (sourceColumn !== undefined) {
        return sourceColumn;
    }
    if (getEnumProperty(pd) === true) {
        return true;
    }
    return undefined;
}
